import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Health-schedule client (T-0105 / E07-F05).
 *
 * Wires the schedule screen to the dedicated milestone register
 * endpoint shipped in PR #129 (T-0075 / C20-70):
 *
 *   GET /api/v1/engagements/:engagement_id/health/schedule
 *     → { milestones: MilestoneDto[] }
 *
 * `MilestoneDto` carries:
 *   { id, engagement_id, name, planned_date, forecast_date,
 *     actual_date, depends_on_milestone_id, notes, origin,
 *     original_ai_confidence, slip_days, slip_state,
 *     upstream_slip_flag, created_by, created_at, updated_at }
 *
 * Slip / slip-state derivation
 * ----------------------------
 * The backend deterministically derives `slip_days` + `slip_state`
 * (R6 invariant — see services/health-schedule/status-derivation.ts);
 * the UI also runs its own `classifySlip()` against (planned,
 * projected, today) for the at-risk/critical visual banding the
 * Gantt + chip components expect (their state vocabulary is
 * `done | on-track | at-risk | critical | unknown`, distinct from
 * the backend's `on_track | at_risk | slipped` enum which captures
 * the milestone's overall state more coarsely). Both agree on the
 * 7-day at-risk threshold.
 *
 * Dependency cascade
 * ------------------
 * The backend marks `upstream_slip_flag` for single-hop dependency
 * cascades. Multi-hop / full CPM critical-path is reserved for T-0130
 * (per T-0075 v1 simplifications). The flag rides through to
 * `ScheduleMilestone.upstreamSlipFlag` so the UI can surface it on
 * the row tooltip while the multi-hop critical-path placeholder
 * remains in the existing `PhaseRollup` block.
 */

export type ScheduleOriginBackend =
  | 'manual'
  | 'ai_proposed'
  | 'ai_extracted_confirmed_as_is'
  | 'ai_extracted_edited_by_consultant';

const ScheduleOriginSchema = z.enum([
  'manual',
  'ai_proposed',
  'ai_extracted_confirmed_as_is',
  'ai_extracted_edited_by_consultant',
]);

const SlipStateBackendSchema = z.enum(['on_track', 'at_risk', 'slipped']);
export type SlipStateBackend = z.infer<typeof SlipStateBackendSchema>;

/** T-0240: 'done' once an actual date is recorded, else the slip band. */
const MilestoneStatusBackendSchema = z.enum(['done', 'on_track', 'at_risk', 'slipped']);
export type MilestoneStatusBackend = z.infer<typeof MilestoneStatusBackendSchema>;

const MilestoneDtoSchema = z.object({
  id: z.string().uuid(),
  engagement_id: z.string().uuid(),
  name: z.string(),
  planned_date: z.string(),
  forecast_date: z.string().nullable(),
  actual_date: z.string().nullable(),
  depends_on_milestone_id: z.string().uuid().nullable(),
  notes: z.string().nullable(),
  origin: ScheduleOriginSchema,
  original_ai_confidence: z.number().nullable(),
  slip_days: z.number(),
  slip_state: SlipStateBackendSchema,
  upstream_slip_flag: z.boolean(),
  // T-0240: the IV&V gate flag and the derived status. Optional so an older
  // API still parses.
  is_ivv_gate: z.boolean().optional(),
  status: MilestoneStatusBackendSchema.optional(),
  // T-0130 (PR #155) — present only when `?include=critical_path`. Pure
  // derivation off the milestone dependency DAG; cycle-defensive.
  on_critical_path: z.boolean().optional(),
  total_float_days: z.number().int().optional(),
  created_by: z.string().uuid(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type MilestoneDto = z.infer<typeof MilestoneDtoSchema>;

const ListMilestonesResponseSchema = z.object({
  milestones: z.array(MilestoneDtoSchema),
  // T-0130: top-level critical_path chain (ordered list of milestone ids).
  critical_path: z.array(z.string().uuid()).optional(),
});

/**
 * UI-facing projected milestone row consumed by the Gantt, date-change
 * log, and KPI tiles. The shape is preserved from the pre-Wave-9
 * register-backed projection so existing components don't churn; the
 * data now flows from the dedicated milestone endpoint.
 */
export interface ScheduleMilestone {
  /** Backend milestone row id. */
  registerEntryId: string;
  /** Mono-typed milestone code derived from the row name. */
  code: string;
  /** Display label (e.g. "Cutover"). */
  name: string;
  /** Optional gate description — not modelled by the backend yet. */
  gate: string | null;
  /** Planned date (SOW baseline) as a Date, or null when missing. */
  planned: Date | null;
  /**
   * Currently projected date — prefer forecast, fall back to actual
   * (a completed milestone's actual_date is its terminal "where this
   * landed" date).
   */
  projected: Date | null;
  /** Optional source citation — not on the schedule_milestones row. */
  source: string | null;
  /** Optional trigger description — not on the schedule_milestones row. */
  trigger: string | null;
  /** Provenance origin (carried from milestone row). */
  origin: ScheduleOriginBackend;
  /** AI confidence (0..1) when the row carries an AI proposal trace. */
  reevalConfidence: number | null;
  /**
   * Override tag (e.g. "verified" / "consultant_edited") applied on
   * the most recent date change. v1 backend stores override metadata
   * on the milestone_date_history rows; the composite list endpoint
   * does NOT denormalize the latest tag onto the milestone row. v1 UI
   * carries `null` here; the future date-history endpoint can fill it.
   */
  overrideTag: string | null;
  /** Backend's single-hop upstream slip flag (T-0075). */
  upstreamSlipFlag: boolean;
  /** Backend's slip days (signed; same semantics as `classifySlip`). */
  slipDaysBackend: number;
  /** Backend's slip-state band. */
  slipStateBackend: SlipStateBackend;
  /** T-0130: pure-deriver "is this milestone on the critical path?" flag.
   *  Present only when the consumer asked for `?include=critical_path`. */
  onCriticalPath: boolean;
  /** T-0130: slack days vs. the longest planned chain (CPM `total_float`).
   *  Zero when the milestone is on the critical path. */
  totalFloatDays: number | null;
  /** The wire dates as the API sent them (calendar dates), for the charts. */
  plannedDate: string;
  forecastDate: string | null;
  actualDate: string | null;
  /** T-0240: a person marked this milestone an IV&V gate. */
  isIvvGate: boolean;
  /** T-0240: the API's status ('done' once actual); slip band when absent. */
  status: MilestoneStatusBackend;
}

/**
 * Mono code: backend names like "Cutover" don't carry the prototype's
 * "M-06" mono prefix. Derive a stable mono code from the first row of
 * each engagement's name set so the Gantt / date-change log can render
 * the familiar two-column "code · name" layout. v1 fallback: first 6
 * chars of the milestone id uppercased.
 */
function deriveCode(row: MilestoneDto): string {
  const idPrefix = row.id.replace(/-/g, '').slice(0, 6).toUpperCase();
  return idPrefix;
}

function parseDate(v: string | null): Date | null {
  if (v === null) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function projectMilestoneDto(row: MilestoneDto): ScheduleMilestone {
  return {
    registerEntryId: row.id,
    code: deriveCode(row),
    name: row.name,
    gate: null,
    planned: parseDate(row.planned_date),
    projected: parseDate(row.forecast_date) ?? parseDate(row.actual_date),
    source: null,
    trigger: null,
    origin: row.origin,
    reevalConfidence: row.original_ai_confidence,
    overrideTag: null,
    upstreamSlipFlag: row.upstream_slip_flag,
    slipDaysBackend: row.slip_days,
    slipStateBackend: row.slip_state,
    onCriticalPath: row.on_critical_path === true,
    totalFloatDays: typeof row.total_float_days === 'number' ? row.total_float_days : null,
    plannedDate: row.planned_date,
    forecastDate: row.forecast_date,
    actualDate: row.actual_date,
    isIvvGate: row.is_ivv_gate === true,
    status: row.status ?? (row.actual_date !== null ? 'done' : row.slip_state),
  };
}

export interface UseScheduleResult {
  milestones: ScheduleMilestone[];
  /** T-0130: ordered list of milestone ids on the critical path; empty
   *  when the consumer didn't ask for `?include=critical_path` or when no
   *  dependency edges exist. */
  criticalPathChain: string[];
  isLoading: boolean;
  error: unknown;
  /** True when the backend returns zero milestone rows (empty engagement). */
  isBackendEmpty: boolean;
}

export function useScheduleMilestonesQuery(engagementId: string | undefined): UseScheduleResult {
  // Pass `?include=critical_path` so the response carries CPM data per
  // milestone + the top-level chain. T-0130: forward-compatible — without
  // the include the response is byte-identical to T-0075.
  const list = useQuery({
    queryKey: ['health', 'schedule', engagementId, 'with-cpm'] as const,
    queryFn: () =>
      apiClient(
        `/api/v1/engagements/${encodeURIComponent(engagementId!)}/health/schedule`,
        ListMilestonesResponseSchema,
        { query: { include: 'critical_path' } },
      ).then((res) => ({
        milestones: res.milestones.map(projectMilestoneDto),
        criticalPathChain: res.critical_path ?? [],
      })),
    enabled: Boolean(engagementId),
    staleTime: 60_000,
  });
  return {
    milestones: list.data?.milestones ?? [],
    criticalPathChain: list.data?.criticalPathChain ?? [],
    isLoading: list.isLoading,
    error: list.error,
    isBackendEmpty: !list.isLoading && (list.data?.milestones.length ?? 0) === 0,
  };
}

// ===========================================================================
// Slip classification — preserved for back-compat with existing UI.
// Re-derives in JS from (planned, projected, today). Agrees with the
// backend on the 7-day boundary.
// ===========================================================================

export type SlipState = 'done' | 'on-track' | 'at-risk' | 'critical' | 'unknown';

export function classifySlip(
  planned: Date | null,
  projected: Date | null,
  today: Date,
): { slipDays: number | null; state: SlipState } {
  if (!planned || !projected) return { slipDays: null, state: 'unknown' };
  const MS_PER_DAY = 86_400_000;
  const slipDays = Math.round((projected.getTime() - planned.getTime()) / MS_PER_DAY);
  const isPast = planned.getTime() <= today.getTime();
  if (slipDays === 0) {
    return { slipDays, state: isPast ? 'done' : 'on-track' };
  }
  if (slipDays > 0 && slipDays <= 7) return { slipDays, state: 'at-risk' };
  if (slipDays > 7) return { slipDays, state: 'critical' };
  return { slipDays, state: 'on-track' };
}

export const SCHEDULE_FILTERS = ['all', 'in-progress', 'past', 'future', 'at-risk'] as const;
export type ScheduleFilter = (typeof SCHEDULE_FILTERS)[number];

export function applyScheduleFilter(
  milestones: ScheduleMilestone[],
  filter: ScheduleFilter,
  today: Date,
): ScheduleMilestone[] {
  if (filter === 'all') return milestones;
  return milestones.filter((m) => {
    if (!m.planned && !m.projected) return false;
    const { state } = classifySlip(m.planned, m.projected, today);
    if (filter === 'at-risk') return state === 'at-risk' || state === 'critical';
    if (filter === 'past') return Boolean(m.projected && m.projected.getTime() <= today.getTime());
    if (filter === 'future') return Boolean(m.planned && m.planned.getTime() > today.getTime());
    if (filter === 'in-progress') {
      return Boolean(
        m.planned &&
        m.projected &&
        m.planned.getTime() <= today.getTime() &&
        m.projected.getTime() > today.getTime(),
      );
    }
    return false;
  });
}

// ===========================================================================
// Milestone writes (T-0075 backends — client wiring added in Phase 1)
// ===========================================================================

export interface CreateMilestoneBody {
  name: string;
  planned_date: string;
  forecast_date?: string | null;
  notes?: string | null;
  is_ivv_gate?: boolean;
}

const MilestoneWriteResultSchema = z.object({ id: z.string().optional() }).passthrough();

export function useCreateMilestoneMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateMilestoneBody) =>
      apiClient(
        `/api/v1/engagements/${encodeURIComponent(engagementId!)}/health/schedule`,
        MilestoneWriteResultSchema,
        { method: 'POST', body },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['health', 'schedule', engagementId] });
    },
  });
}

/** True for a milestone the AI proposed: its date changes need a reason (the API's override). */
export function isAiDerivedMilestone(origin: ScheduleOriginBackend): boolean {
  return origin !== 'manual';
}

/**
 * PATCH …/health/schedule/:milestone_id (T-0075, gate from T-0240): send
 * only the fields that change. A date change on an AI-derived milestone
 * carries `override` (tag + a reason of 10 characters or more); the API
 * refuses it without one.
 */
export interface UpdateMilestoneBody {
  planned_date?: string;
  forecast_date?: string | null;
  actual_date?: string | null;
  is_ivv_gate?: boolean;
  override?: { tag: string; rationale: string };
}

export function useUpdateMilestoneMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ milestoneId, body }: { milestoneId: string; body: UpdateMilestoneBody }) =>
      apiClient(
        `/api/v1/engagements/${encodeURIComponent(engagementId!)}/health/schedule/${encodeURIComponent(milestoneId)}`,
        MilestoneWriteResultSchema,
        { method: 'PATCH', body },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['health', 'schedule', engagementId] });
    },
  });
}
